import type { RouteRecordRaw } from "vue-router";

/**
 * Review Requests — Phase 1's five administration screens, plus Phase 2B's
 * request list and composer.
 *
 * The five configuration surfaces exist first so a human can correct the
 * seeded roster before anything prices a review from it; `review-compose`
 * refuses to open until that has happened.
 *
 * Auth routes — inside the auth shell in `router/index.ts`, so `requiresAuth`
 * is inherited from the parent and must NOT be re-declared here.
 *
 * Two shapes live here, and the difference matters:
 *
 * 1. **Company-wide** — `review-requests` and `review-bands` both render
 *    `ReviewRequestsView.vue`, which shows them as two tabs of one screen and
 *    navigates between them by route. They keep separate paths precisely so
 *    the strip can navigate rather than toggle local state, which is what
 *    keeps `/review-requests/bands` a shareable URL.
 * 2. **Client- and scholar-scoped** — settings, roster, products and terms are
 *    no longer a person's entry point: settings (with roster and products
 *    inside it) is a tab on `Business/ClientForm.vue`, and terms is a tab on
 *    `ManageData/Scholars/ScholarForm.vue`, because each is a fact about the
 *    record being edited. Their routes stay because they are still deep-linked
 *    — the composer's blocked-scholar fixes (`Requests/resolutionRules.ts`,
 *    `Requests/Composer.vue`) navigate straight to a specific client's roster
 *    or a specific scholar's terms. Do not delete them to "tidy up".
 *
 *    `review-compose` itself is reached the same way: not from a client's own
 *    screens, but from the Requests Dashboard's "New review request" button
 *    (`Requests/NewRequestDialog.vue`), whose client picker resolves a client
 *    and navigates here by name (spec 2026-09-26, D1 — one entry point).
 *
 * `meta.moduleKey: 'review_requests'` gates every route on the module grant
 * (mirrors the backend's `require_module(MODULE_NAME)` on every handler in
 * this scope). The client- and scholar-scoped sector guard is enforced
 * server-side only — this key is deliberately coarse, matching the module
 * grant, not the per-request sector check.
 */
export const reviewRequestsRoutes: RouteRecordRaw[] = [
  {
    // Phase 2B: the request list, on the "Requests Dashboard" tab. One of the
    // two routes in this file with no id in its path, which is exactly why the
    // backend behind it FILTERS by sector instead of denying
    // (`handlers/request_read.rs`) — a 404 on a LIST would confirm that
    // out-of-sector clients have requests at all.
    path: "/review-requests",
    name: "review-requests",
    component: () => import("@/views/ReviewRequests/ReviewRequestsView.vue"),
    meta: { moduleKey: "review_requests" },
  },
  {
    // Phase 2B: ONE request, after it was sent (Task 15). Both the list's
    // row link (`RequestList.vue`) and the confirm step's post-submit
    // navigation (`composer/StepConfirm.vue`) address it BY NAME, so the
    // `path` below can change without either of them going stale.
    path: "/review-requests/requests/:requestId",
    name: "review-request-detail",
    component: () =>
      import("@/views/ReviewRequests/Requests/RequestDetail.vue"),
    props: true,
    meta: { moduleKey: "review_requests" },
  },
  {
    // The same shell as `review-requests` above, on its "Category Bands" tab.
    // Kept as its own path so the strip navigates instead of holding local
    // state — which is what lets the composer's `fixBands` action, and a
    // pasted link, land directly on this tab.
    path: "/review-requests/bands",
    name: "review-bands",
    component: () => import("@/views/ReviewRequests/ReviewRequestsView.vue"),
    meta: { moduleKey: "review_requests" },
  },
  {
    path: "/review-requests/clients/:clientId/settings",
    name: "review-client-settings",
    component: () =>
      import("@/views/ReviewRequests/Settings/ClientReviewSettings.vue"),
    props: true,
    meta: { moduleKey: "review_requests" },
  },
  {
    path: "/review-requests/clients/:clientId/roster",
    name: "review-roster",
    component: () => import("@/views/ReviewRequests/Roster/ClientRoster.vue"),
    props: true,
    meta: { moduleKey: "review_requests" },
  },
  {
    path: "/review-requests/clients/:clientId/products",
    name: "review-products",
    component: () =>
      import("@/views/ReviewRequests/Products/ClientProducts.vue"),
    props: true,
    meta: { moduleKey: "review_requests" },
  },
  {
    // Phase 2B: the request composer. Same shape as the five above — the
    // client is in the path, so the sector guard the backend applies to every
    // call this screen makes is the same one that guards the admin screens.
    // Reached from the Requests Dashboard's "New review request" button
    // (`Requests/NewRequestDialog.vue`), which resolves the client via its
    // picker and navigates here by name — the ONE entry point (D1). The
    // Clients page dropdown no longer links here.
    //
    // `?draft=<id>` resumes a draft: the dashboard's draft rows link here with
    // it, and the composer writes it itself once its draft exists
    // (`Requests/navigationRules.ts` says why it is a query, not a path
    // segment).
    path: "/review-requests/clients/:clientId/compose",
    name: "review-compose",
    component: () => import("@/views/ReviewRequests/Requests/Composer.vue"),
    props: (route) => ({
      clientId: route.params.clientId,
      draftId: route.query.draft,
    }),
    meta: { moduleKey: "review_requests" },
  },
  {
    path: "/review-requests/scholars/:scholarId/terms",
    name: "review-terms",
    component: () => import("@/views/ReviewRequests/Terms/ScholarTerms.vue"),
    props: true,
    meta: { moduleKey: "review_requests" },
  },
];
